import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { clientDisplayName, IPriorityClient, READING_LABEL } from '@innerhive-internal/innerhive-common';
import { IAvatarIdentity } from '../../avatar/IAvatarIdentity';
import { BRAND, CHART_INK, READING_COLOR } from './agencyChartTheme';
import { ClientReadingAvatar } from './ClientReadingAvatar';
import { InsightsNarration } from './InsightsNarration';

const useStyles = createUseStyles({
    rows: {
        display: 'flex',
        flexDirection: 'column' as const
    },

    row: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        width: '100%',
        padding: '11px 8px',
        border: 0,
        borderTop: `1px solid ${BRAND.tealEdge}`,
        borderRadius: 8,
        background: 'none',
        font: 'inherit',
        textAlign: 'left' as const,
        cursor: 'pointer',

        '&:first-child': {
            borderTop: 'none'
        },
        '&:hover, &:focus-visible': {
            background: BRAND.tealWash
        },

        '@media print': {
            breakInside: 'avoid'
        }
    },

    body: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 3,
        flex: 1,
        minWidth: 0
    },

    head: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap' as const,
        gap: '2px 10px'
    },

    name: {
        fontSize: 14,
        fontWeight: 600,
        color: BRAND.deepTeal
    },

    // The reading in words beside the name, since the ring alone is never allowed to carry it.
    reading: {
        fontSize: 11.5,
        fontWeight: 600,
        letterSpacing: '0.03em',
        textTransform: 'uppercase' as const
    },

    why: {
        fontSize: 13,
        lineHeight: 1.45,
        color: CHART_INK.strongLabel
    },

    quiet: {
        fontSize: 12.5,
        color: CHART_INK.label
    },

    more: {
        marginTop: 10,
        padding: 0,
        border: 0,
        background: 'none',
        font: 'inherit',
        fontSize: 12.5,
        color: CHART_INK.label,

        '&:enabled': {
            color: BRAND.deepTeal,
            cursor: 'pointer'
        },
        '&:enabled:hover, &:enabled:focus-visible': {
            textDecoration: 'underline'
        }
    }
});

/**
 * The top of the page: the few clients to deal with first, each in a line or two. The reading they
 * are drawn in everywhere, and why they are here. The row opens the same detail the map and every
 * list open.
 */
export const NeedsAttentionReport: FC<{
    clients: IPriorityClient[];
    /** Clients with an incident the list had no room for. */
    unlistedIncidents: number;
    /** How each client is drawn, with the audited link to a hosted photo. */
    clientAvatar: (careMapId: string) => IAvatarIdentity | undefined;
    onSelectClient: (careMapId: string) => void;
    /** Narrows the page to the clients drawn red, for the incidents the list left out. Absent on paper. */
    onShowIncidents?: () => void;
}> = ({ clients, unlistedIncidents, clientAvatar, onSelectClient, onShowIncidents }) => {
    const classes = useStyles();
    const narration = useInstance(InsightsNarration);

    if (clients.length === 0) {
        return (
            <div className={classes.quiet} data-id="needs-attention-empty">
                Nobody needs dealing with first right now: no incident on the record in the last few weeks, and nobody worth watching.
            </div>
        );
    }

    return (
        <>
            <div className={classes.rows} data-id="needs-attention">
                {clients.map(client => {
                    const name = clientDisplayName(client.clientName);
                    return (
                        <button
                            key={client.careMapId}
                            type="button"
                            className={classes.row}
                            data-id="needs-attention-row"
                            data-reading={client.reading}
                            onClick={() => onSelectClient(client.careMapId)}
                        >
                            <ClientReadingAvatar size={36} identity={clientAvatar(client.careMapId)} name={name} reading={client.reading} />
                            <span className={classes.body}>
                                <span className={classes.head}>
                                    <span className={`${classes.name} mp-mask`} data-id="needs-attention-name">{name}</span>
                                    <span className={classes.reading} style={{ color: client.reading === 'incident' ? READING_COLOR.incident : BRAND.coralInk }}>
                                        {READING_LABEL[client.reading]}
                                    </span>
                                </span>
                                {/* Masked, unlike a note's own text, because a finding can name a family member along with the client. */}
                                <span className={`${classes.why} mp-mask`} data-id="needs-attention-why">{narration.describePriorityWhy(client)}</span>
                            </span>
                        </button>
                    );
                })}
            </div>

            {unlistedIncidents > 0 && (
                <button type="button" className={classes.more} data-id="needs-attention-more" disabled={!onShowIncidents} onClick={onShowIncidents}>
                    {unlistedIncidents} more {unlistedIncidents === 1 ? 'client' : 'clients'} had an incident{onShowIncidents ? ': see them all' : ''}.
                </button>
            )}
        </>
    );
};
